import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import getGridWidthClass from '../../utils/getGridWidthClass';
import { InputText, InputDateRange, InputMultiSelect, InputLabelWrapper } from '../';

const InputGroup = ({
  inputMeta,
  values,
  onValuesChange,
  sorting: { sortBy, sortDirection },
  onSortingChange,
  isSortingEnabled,
}) => {
  const handleFilterLabelClick = name => {
    if (!isSortingEnabled) return;

    onSortingChange({
      sortBy: name,
      sortDirection: sortBy === name && sortDirection === 'ascending' ? 'descending' : 'ascending',
    });
  };

  const renderFieldInputComponent = ({ name, displayName, inputProps, isSortable, inputType }) => {
    const commonProps = {
      id: name,
      key: name,
      label: displayName,
      isSortable: isSortable && isSortingEnabled,
      sortDirection: sortBy === name ? sortDirection : 'none',
      onLabelClick: () => handleFilterLabelClick(name),
    };

    const handleChange = newValue => onValuesChange({ ...values, [name]: newValue });
    const handleDateChange = ({ startDate, endDate }) =>
      onValuesChange({ ...values, [name]: { startDate, endDate } });

    switch (inputType) {
      case 'Text':
        return (
          <InputText
            {...commonProps}
            className="min-w-[84px] py-1 px-2 text-xs"
            value={values[name]}
            onChange={handleChange}
          />
        );
      case 'MultiSelect':
        return (
          <InputMultiSelect
            {...commonProps}
            className="min-w-[84px] py-1 px-2 text-xs"
            value={values[name]}
            onChange={handleChange}
            options={inputProps.options}
          />
        );
      case 'DateRange':
        return (
          <InputDateRange
            {...commonProps}
            className="min-w-[112px] py-1 px-2 text-xs"
            value={values[name]}
            onChange={handleDateChange}
          />
        );
      case 'None':
        return (
          <InputLabelWrapper
            {...commonProps}
            className="min-w-[60px] text-xs"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative w-full overflow-hidden">
      <div className="w-full overflow-x-auto pb-0.5">
        <div className="flex min-w-full flex-nowrap gap-1.5 px-1.5 md:flex-wrap">
          {inputMeta.map((meta, i) => (
            <div
              key={meta.name}
              className={classnames('min-w-max flex-shrink-0', i && 'pl-0.5')}
            >
              {renderFieldInputComponent(meta)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

InputGroup.propTypes = {
  inputMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      gridCol: PropTypes.oneOf([...Array(12).keys()].map(x => x + 1)).isRequired,
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  values: PropTypes.object.isRequired,
  onValuesChange: PropTypes.func.isRequired,
  sorting: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDirection: PropTypes.oneOf(['ascending', 'descending', 'none']),
  }).isRequired,
  onSortingChange: PropTypes.func.isRequired,
  isSortingEnabled: PropTypes.bool.isRequired,
};

export default InputGroup;
